import React from 'react';
import { PageWrapper, SeoHead } from 'layouts';
import { ParsedUrlQuery } from 'querystring';
import { GetStaticProps, NextPage } from 'next';
import {
  getCategoryPostsData,
  GetPageDataReturnData,
} from '../../../../lib/posts';
import { CategoryUrls, logger, sanitizeCategorySeoHead } from 'utils';
import { EventIds } from 'lib/EventIds';
import { getCategoryPageData } from '..';
import { FullCategoryNodeFragment } from 'types';
import { CategoryPosts } from 'components/Category/CategoryPosts';

export type CategoryPostsPageProps = { posts: GetPageDataReturnData } & {
  categoryData: FullCategoryNodeFragment;
} & Record<string, unknown>;

const CategoryPostsPage: NextPage<CategoryPostsPageProps> = (props) => {
  const sanitizedSeoHead = sanitizeCategorySeoHead(
    props.categoryData.seo?.canonical,
    props.categoryData.seo?.fullHead,
    props.posts.page,
    props.posts.totalAmountOfPosts || 1
  );
  return (
    <PageWrapper headerColor="white">
      <SeoHead seoHead={sanitizedSeoHead} />
      <CategoryPosts {...props} />
    </PageWrapper>
  );
};

export async function getStaticPaths() {
  return {
    // Generate 3 pages by default (like for pages)
    // paths: CategoryUrls.flatMap((categoryUrl) => [
    //   `${categoryUrl}page/1`,
    //   `${categoryUrl}page/2`,
    //   `${categoryUrl}page/3`,
    // ]),
    // TODO: bring back when Apollo is using GET requests
    paths: [],
    fallback: 'blocking',
  };
}

export interface CategoryPostsPageParams extends ParsedUrlQuery {
  page: string;
  category: string;
  categoryId?: string;
}

export const getStaticProps: GetStaticProps<
  CategoryPostsPageProps,
  CategoryPostsPageParams
> = async (props) => {
  // Use will be redirected to homepage automatically if category param not present
  const categorySlug = props.params?.category as string;

  // First try to get category data to get the associated ID
  const { data: categoryData, error: categoryError } =
    await getCategoryPageData(categorySlug, props.locale);
  if (categoryData !== null && categoryError) {
    logger.error(
      'Getting error on Category post fetch',
      EventIds.CategoryPageError,
      categoryError
    );
  }
  if (categoryData === null || !props.params) {
    return {
      redirect: {
        destination: props.locale !== 'en' ? `/${props.locale}` : '/',
        permanent: true,
      },
    };
  }

  // Then get category posts
  const { posts, totalAmountOfPosts, page, error } = await getCategoryPostsData(
    {
      ...props,
      params: {
        page: props.params.page,
        category: categorySlug,
        categoryId: categoryData.databaseId.toString(),
      },
    }
  );

  return {
    props: {
      categoryData,
      posts: { error, page, posts, totalAmountOfPosts },
    },
    revalidate: 600, // In seconds
  };
};

export default CategoryPostsPage;
